import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth, SESSION_KEY } from "@/context/AuthContext";
import { rememberAccount } from "@/lib/accounts";
import { toast } from "sonner";
import { MfaStep } from "@/components/MfaStep";
import { useDemo } from "@/hooks/useDemo";

export default function Login() {
  const { login, setUser } = useAuth();
  const demo = useDemo();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [email, setEmail] = useState(state?.email || "");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const [mfaToken, setMfaToken] = useState(state?.mfaToken || null);
  const [pickRole, setPickRole] = useState(null);

  const enter = (u) => {
    setUser(u);
    rememberAccount(u);
    try { localStorage.setItem(SESSION_KEY, JSON.stringify({ id: u.id, at: Date.now() })); } catch { /* storage unavailable */ }
    toast.success(`Welcome back, ${u.name}`);
    navigate(u.role === "clinician" ? "/clinician" : "/client");
  };

  const submit = async (e, role) => {
    e?.preventDefault();
    setErr("");
    setLoading(true);
    try {
      const u = await login(email, password, role);
      if (u.pick_role) return setPickRole(u.pick_role);
      if (u.mfa_required) return setMfaToken(u.mfa_token);
      enter(u);
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  };

  const rolePicker = pickRole && (
    <div className="space-y-3" data-testid="login-pick-role">
      <p className="text-sm text-stone-600">This email has two accounts. Which one do you want to open?</p>
      {pickRole.map((r) => (
        <button key={r.role} onClick={(e) => submit(e, r.role)} disabled={loading} data-testid={`login-pick-${r.role}`}
          className="w-full flex items-center justify-between px-4 py-3 rounded-xl border border-stone-300 hover:border-amber-600 hover:bg-amber-50 transition-colors">
          <span className="text-sm font-semibold text-stone-800 capitalize">{r.role}</span><span className="text-xs text-stone-500">{r.name}</span>
        </button>
      ))}
      <button onClick={() => setPickRole(null)} className="text-xs text-stone-500 hover:underline" data-testid="login-pick-back">Back</button>
    </div>
  );

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <div className="warm-card p-8">
        <h1 className="font-display text-3xl font-extrabold text-stone-900 mb-1">Welcome back</h1>
        <p className="text-sm text-stone-600 mb-6">Sign in to continue your journey.</p>
        {mfaToken ? <MfaStep mfaToken={mfaToken} onVerified={enter} onCancel={() => setMfaToken(null)} /> : pickRole ? rolePicker : (
        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Email</label>
            <input
              data-testid="login-email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition"
            />
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-widest text-stone-600">Password</label>
            <input
              data-testid="login-password"
              type="password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-1 w-full px-4 py-3 rounded-xl border border-stone-300 focus:border-amber-600 focus:ring-2 focus:ring-amber-200 outline-none transition"
            />
          </div>
          {err && (
            <div data-testid="login-error" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">
              {err}
            </div>
          )}
          <button data-testid="login-submit" type="submit" disabled={loading} className="amber-btn w-full">
            {loading ? "Signing in…" : "Sign in"}
          </button>
        </form>
        )}
        <div className="text-sm text-stone-500 text-center mt-6 space-y-1">
          <div>
            <Link to="/forgot-password" data-testid="login-forgot-link" className="text-stone-600 hover:text-amber-800 hover:underline">Forgot your password?</Link>
          </div>
          <div>
            New here?{" "}
            <Link to="/register" data-testid="login-to-register" className="text-amber-800 font-semibold hover:underline">
              Create an account
            </Link>
          </div>
          <div>
            Just looking?{" "}
            <button onClick={() => demo.start("client")} disabled={demo.busy} data-testid="login-try-demo" className="text-amber-800 font-semibold hover:underline disabled:opacity-60">
              {demo.busy ? "Planting your demo garden…" : "Try the demo"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
